import React, {createContext, ReactNode, RefObject, useCallback, useContext, useRef} from "react";
import {TextButton} from "@/app/components/TextButton";
import {AnalyticsTags} from "@/lib/analytics/clickTracker";
import {useFileUpload} from "@/hooks/use-file-upload";
import {IMAGE_VALIDATION, isImageFile} from "@/lib/validation";
import type {AttachmentInfo} from "@/hooks/use-unified-file-upload";

export type {AttachmentInfo}

interface FileUploadContextValue {
  fileInputRef: RefObject<HTMLInputElement | null>
  onUpdate: (fullPath: string | undefined) => void
}

const FileUploadContext = createContext<FileUploadContextValue | null>(null);

const useFileUploadContext = () => {
  const ctx = useContext(FileUploadContext);
  if (!ctx) {
    throw new Error("useFileUploadContext must be used within <FileUpload></FileUpload>")
  }
  return ctx
}


export const FileUploadProvider = ({props, children}: { props: FileUploadContextValue, children: ReactNode }) => {
  return <FileUploadContext.Provider value={props}>
    {children}
  </FileUploadContext.Provider>
};

export interface FileUploadProps {
  /** URL signer function for generating presigned upload URLs */
  urlSigner: (extension: string, fileType: string, fileName: string, fileSize: number) => Promise<{
    fullPath: string,
    signedUrl: string
  }>
  /** Callback when a single file is uploaded (returns image URL or attachment ID) */
  onUpdate: (fullPath: string | undefined) => void
  /** Callback when multiple files are uploaded */
  onMultipleUpdate?: (fullPaths: string[]) => void
  /** Callback when an image is uploaded */
  onImageUploaded?: (url: string) => void
  /** Callback when a document is uploaded */
  onAttachmentUploaded?: (attachment: AttachmentInfo) => void
  /** Callback when an image file is selected (before upload). If provided, the component will NOT upload the image automatically — the caller is responsible for uploading. */
  onImageSelected?: (file: File) => void
  maxSize?: number,
  children?: ReactNode,
  allowedExtensions?: string[],
  /** Allowed file types for the input accept attribute - caller controls what types are allowed */
  allowedTypes?: string[],
  noSuccessToast?: boolean,
  tags?: AnalyticsTags,
  multiple?: boolean,
  disabled?: boolean,
}

function resolveValidationRules(
  allowedTypes: string[] | undefined,
  allowedExtensions: string[] | undefined
): { acceptValues: string[]; mimeTypes: string[]; extensions: string[] } {
  const acceptValues = (allowedTypes ?? [...IMAGE_VALIDATION.allowedTypes])
    .map((value) => value.trim())
    .filter(Boolean)

  const mimeTypes: string[] = []
  const extensions = new Set(
    (allowedExtensions ?? [])
      .map((value) => value.trim().toLowerCase())
      .filter(Boolean)
  )

  for (const token of acceptValues) {
    if (token.startsWith('.')) {
      const extension = token.slice(1).toLowerCase()
      if (extension) {
        extensions.add(extension)
      }
      continue
    }
    mimeTypes.push(token)
  }

  return {acceptValues, mimeTypes, extensions: [...extensions]}
}

export function FileUpload(props: FileUploadProps) {
  const {
    urlSigner,
    onUpdate,
    onMultipleUpdate,
    onImageUploaded,
    onAttachmentUploaded,
    onImageSelected,
    children,
    allowedExtensions,
    allowedTypes,
    maxSize,
    noSuccessToast,
    tags,
    multiple,
    disabled,
  } = props
  const fileInputRef = useRef<HTMLInputElement>(null);

  const {
    acceptValues,
    mimeTypes: allowedMimeTypesForValidation,
    extensions: mergedAllowedExtensions,
  } = resolveValidationRules(allowedTypes, allowedExtensions)

  const {uploadFile} = useFileUpload(
    urlSigner,
    {
      maxSize,
      allowedTypes: allowedMimeTypesForValidation.length > 0 ? allowedMimeTypesForValidation : undefined,
      allowedExtensions: mergedAllowedExtensions.length > 0 ? mergedAllowedExtensions : undefined,
    },
    {noSuccessToast: noSuccessToast ?? true}
  );

  const handleFileChange = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    // Upload each file and call appropriate callbacks
    const allPaths: string[] = []
    for (const file of files) {
      const isImage = isImageFile(file)

      // If onImageSelected is provided and the file is an image, delegate to caller
      // (e.g., to show a description dialog before uploading)
      if (isImage && onImageSelected) {
        onImageSelected(file)
        event.target.value = '';
        return
      }

      const path = await uploadFile(file)
      if (path) {
        if (isImage) {
          onImageUploaded?.(path)
        } else {
          onAttachmentUploaded?.({id: path, fileName: file.name})
        }
        allPaths.push(path)
      }
    }

    // Call onUpdate/onMultipleUpdate with all paths
    if (allPaths.length === 1) {
      onUpdate(allPaths[0])
    } else if (allPaths.length > 1) {
      onMultipleUpdate?.(allPaths)
    }

    // Reset input
    event.target.value = '';
  }, [uploadFile, onUpdate, onMultipleUpdate, onImageUploaded, onAttachmentUploaded, onImageSelected]);

  return (
    <FileUploadProvider props={{fileInputRef, onUpdate}}>
      <div className="flex items-center">
        <div className="flex items-center">
          <input
            type="file"
            ref={fileInputRef}
            className="hidden"
            accept={acceptValues.join(", ")}
            onChange={handleFileChange}
            multiple={multiple}
            disabled={disabled}
            {...(tags ?? {})}
          />
          <div>
            {children}
          </div>
        </div>
      </div>
    </FileUploadProvider>
  );
}

export function FileUploadTrigger({disabled, children}: { disabled?: boolean, children?: ReactNode }) {
  const {fileInputRef} = useFileUploadContext()
  const handleClick = useCallback(() => {
    if (!disabled) {
      fileInputRef.current?.click()
    }
  }, [disabled, fileInputRef]);
  return <div onClick={handleClick}>
    {children}
  </div>
}

export function FileUploadClear() {
  const {onUpdate} = useFileUploadContext()
  return <div>
    <TextButton onClick={() => onUpdate("")} className="text-xs">Clear</TextButton>
  </div>
}
